import { ArrowRight } from "lucide-react";
import type { FormEvent } from "react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { isEmail, submitWaitlist } from "@/lib/waitlist";
import { captureEvent } from "../../lib/analytics";

type Status = "idle" | "loading" | "error" | "success" | "exists";

// The one waitlist capture surface on the site: a bare email, no account, no
// redirect off the landing. On success (or on a duplicate email) we show an
// inline confirmation with an invite CTA — the visitor only lands on the
// dedicated referral page (code, progress, share link) if they choose to
// follow that CTA.
export function WaitlistForm({
  className,
  autoFocus = false,
}: {
  className?: string;
  /** Focus the email field on mount — used when the form opens inside a dialog. */
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlReferralCode = searchParams.get("ref") ?? undefined;

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  // Server-provided failure copy; null falls back to the local i18n strings.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lookup, setLookup] = useState("");
  const feedbackId = useId();
  // Fire `waitlist_started` once per mount, on first interaction with the field
  // — the funnel entry paired with `waitlist_submitted` for start->submit drop.
  const startedRef = useRef(false);
  function markStarted() {
    if (startedRef.current) return;
    startedRef.current = true;
    captureEvent("waitlist_started");
  }

  const loading = status === "loading";
  const joined = status === "success" || status === "exists";

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (loading) return;
    if (!isEmail(email)) {
      setErrorMessage(null);
      setStatus("error");
      return;
    }
    setStatus("loading");
    const waitlistResult = await submitWaitlist(email, urlReferralCode);
    if (waitlistResult.outcome === "error") {
      // A server rejection carries its own copy (invalid email, self-referral,
      // …); an empty message means the request never got a response — say so
      // instead of blaming the email address.
      setErrorMessage(waitlistResult.message || t("waitlist.networkError"));
      setStatus("error");
      return;
    }
    setLookup(waitlistResult.referralCode ?? email.trim());
    setStatus(waitlistResult.outcome);
  }

  if (joined) {
    return (
      <div
        className={cn(
          "mx-auto flex w-full max-w-md flex-col items-center gap-1 text-center",
          className,
        )}
      >
        <p className="text-base font-medium text-foreground">
          {t(status === "exists" ? "waitlist.existsTitle" : "waitlist.successTitle")}
        </p>
        <p className="text-sm text-muted-foreground">{t("waitlist.successBody")}</p>
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="mt-3"
          onClick={() => navigate("/referral", { state: { lookup } })}
        >
          {t("waitlist.inviteCta")}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className={cn("mx-auto flex w-full max-w-md flex-col gap-3", className)}
    >
      {urlReferralCode ? (
        <p className="text-center text-sm text-muted-foreground">{t("waitlist.usingReferral")}</p>
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          type="email"
          name="email"
          autoFocus={autoFocus}
          value={email}
          onFocus={markStarted}
          onChange={(event) => {
            markStarted();
            setEmail(event.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder={t("waitlist.placeholder")}
          aria-label={t("waitlist.placeholder")}
          aria-invalid={status === "error"}
          aria-describedby={status === "error" ? feedbackId : undefined}
          autoComplete="email"
          disabled={loading}
          className="h-12 sm:flex-1"
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={loading}
          trailing={<ArrowRight className="h-4 w-4" aria-hidden />}
          className="shrink-0"
        >
          {loading ? t("waitlist.submitting") : t("waitlist.submit")}
        </Button>
      </div>
      {status === "error" ? (
        <p id={feedbackId} role="alert" className="text-sm text-red-400">
          {errorMessage ?? t("waitlist.error")}
        </p>
      ) : null}
    </form>
  );
}
